import { playerRequest, completeUpload, getUploadPartUrl, getUploadGroupDefaults, initiateUpload, listUserGroups } from '../service'
import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import LoadingButton from '@mui/lab/LoadingButton'
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import MovieCreationOutlinedIcon from '@mui/icons-material/MovieCreationOutlined'
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import axios from 'axios'
import GroupOptions from '../components/common/GroupOptions'
import Messages from '../components/common/Messages'
import { DateTime } from 'luxon'
import { Link as RouterLink } from 'react-router-dom'
import { normalizePlayerPosition, PLAYER_POSITION_OPTIONS } from '../constants/playerPositions'
import { STARTING_SIDE_OPTIONS } from '../constants/gameAppearance'
import { TERMS_PATH, UPLOAD_ATTESTATION, UPLOAD_ATTESTATION_NEEDED, UPLOAD_DISCLAIMER } from '../constants/legal'
import { apiErrorText } from '../service/coaching'

// The API's own sentence (for example, that the Terms of Use changed), else the error as before.
const errorText = err => apiErrorText(err, err?.message || String(err))

const queuedFile = (file, index) => ({
  id: `${file.name}-${file.size}-${file.lastModified}-${index}`,
  file,
  customName: '',
  status: 'queued',
  progress: 0,
  uploadId: null,
  error: null
})

const SOURCE_TYPES = [
  {
    value: 'whole_game',
    label: 'Whole game',
    description: 'The complete game, in one file or several consecutive recordings joined into one video.'
  },
  {
    value: 'shift_clip',
    label: 'Shift clips',
    description: 'Pre-cut full shifts or shift-length clips. Each file stays its own video.'
  },
  {
    value: 'highlight_clip',
    label: 'Highlights',
    description: 'Pre-cut highlights or short clips. Each file stays its own video.'
  }
]

const formatBytes = bytes => {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const unit = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  return `${(bytes / Math.pow(1024, unit)).toFixed(unit >= 2 ? 1 : 0)} ${units[unit]}`
}

function UploadFile () {
  const [players, setPlayers] = useState([])
  const [targets, setTargets] = useState([])
  // Paid analysis starts only on this explicit request, never because players were selected.
  const [autoAnalyze, setAutoAnalyze] = useState(false)
  const analyzeAfterProcessing = autoAnalyze && targets.length > 0
  const [playerError, setPlayerError] = useState('')
  const [gameId, setGameId] = useState(null)
  const [name, setName] = useState('')
  useEffect(() => {
    playerRequest('/players').then(setPlayers).catch(e => setPlayerError(e.message))
  }, [])
  const [message, setMessage] = useState(null)
  const [dateTime, setDateTime] = useState(DateTime.now())
  const [description, setDescription] = useState('')
  const [sourceType, setSourceType] = useState('whole_game')
  const [files, setFiles] = useState([])
  const [combinedUploadId, setCombinedUploadId] = useState(null)
  // D34: several files of a whole game always become one video; shift and highlight clips are never combined.
  const combineFiles = files.length > 1 && sourceType === 'whole_game'
  const fileComponent = useRef(null)
  const [groups, setGroups] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [rightsConfirmed, setRightsConfirmed] = useState(false)
  const [dragActive, setDragActive] = useState(false)

  useEffect(() => {
    let cancelled = false
    const loadGroups = async () => {
      try {
        const available = await listUserGroups({ writable: true })
        const defaults = await getUploadGroupDefaults()
        if (!cancelled) {
          setGroups(available || [])
          setSelectedGroups(defaults || [])
        }
      } catch (err) {
        console.error(err)
      }
    }
    loadGroups()
    return () => {
      cancelled = true
    }
  }, [])

  const addFiles = selected => {
    if (loading || gameId) return

    const videos = Array.from(selected || []).filter(file => file.type.startsWith('video/'))
    setFiles(current => {
      const existing = new Set(current.map(item => `${item.file.name}:${item.file.size}:${item.file.lastModified}`))
      const additions = videos
        .filter(file => !existing.has(`${file.name}:${file.size}:${file.lastModified}`))
        .map((file, index) => queuedFile(file, current.length + index))
      return current.concat(additions)
    })
  }

  const fileChanged = ev => {
    addFiles(ev.target.files)
    ev.target.value = ''
  }

  const handleDrop = ev => {
    ev.preventDefault()
    setDragActive(false)
    addFiles(ev.dataTransfer.files)
  }

  const onGroupChange = values => {
    setSelectedGroups(values)
  }

  const removeFile = id => {
    setFiles(current => current.filter(item => item.id !== id))
  }

  const updateFileName = (id, value) => {
    setFiles(current => current.map(item => (
      item.id === id ? { ...item, customName: value } : item
    )))
  }

  const moveFile = (index, direction) => {
    if (loading || gameId) return
    setFiles(current => {
      const next = [...current]
      const target = index + direction
      if (target < 0 || target >= next.length) return current
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  const uploadName = (item, index) => {
    if (item.customName.trim()) return item.customName.trim()
    if (sourceType === 'whole_game') return name.trim()
    if (sourceType === 'shift_clip') return `${name.trim()} - Shift ${index + 1}`
    return `${name.trim()} - Highlight ${index + 1}`
  }

  const selectSourceType = (_event, value) => {
    if (!value || loading || gameId) return
    setSourceType(value)
  }

  const sourceTypeConfig = SOURCE_TYPES.find(type => type.value === sourceType) || SOURCE_TYPES[0]

  const updateFileState = (id, patch) => {
    setFiles(current => current.map(item => (
      item.id === id ? { ...item, ...patch } : item
    )))
  }

  const uploadCombined = async currentGameId => {
    let uploadId = combinedUploadId
    try {
      if (uploadId) {
        // A failed HTTP response can still mean the file reached storage.
        // Reconcile before retrying so an already processing game is recovered.
        const existing = await completeUpload(uploadId).catch(() => null)
        if (existing && ['queued', 'submitting', 'processing', 'ready'].includes(existing.status)) {
          files.forEach(item => updateFileState(item.id, { status: 'complete', progress: 100, error: null }))
          setMessage({ text: `${files.length} files uploaded and queued as one combined game.`, timeout: -1 })
          return
        }
      }
      if (!uploadId) {
        const initiated = await initiateUpload({
          name: name.trim(),
          gameId: currentGameId,
          date: dateTime.toJSDate(),
          description: description || undefined,
          file: files[0].file,
          files: files.map(item => item.file),
          groups: selectedGroups,
          sourceType: 'whole_game',
          sequenceIndex: null
        })
        uploadId = initiated.upload.id
        setCombinedUploadId(uploadId)
      }
      let failed = 0
      for (let index = 0; index < files.length; index++) {
        const item = files[index]
        if (item.status === 'complete') continue
        try {
          updateFileState(item.id, { status: 'preparing', progress: 0, error: null, uploadId })
          // Obtain a fresh URL for each transfer, even when earlier files took
          // longer than the signed URL lifetime or this is a failed-file retry.
          const { upload_url: uploadUrl } = await getUploadPartUrl(uploadId, index + 1)
          updateFileState(item.id, { status: 'uploading' })
          await axios.put(uploadUrl, item.file, {
            headers: { 'Content-Type': item.file.type || 'video/quicktime' },
            onUploadProgress: progressEvent => {
              if (progressEvent.total) {
                updateFileState(item.id, {
                  progress: Math.min(100, Math.round(progressEvent.loaded * 100 / progressEvent.total))
                })
              }
            }
          })
          updateFileState(item.id, { status: 'complete', progress: 100 })
        } catch (err) {
          failed += 1
          updateFileState(item.id, { status: 'error', error: errorText(err) })
        }
      }
      if (failed) {
        setMessage({ text: `${failed} file${failed === 1 ? '' : 's'} failed. Retry to upload only the failed files. The combined game will wait for every file.`, severity: 'warning', timeout: -1 })
        return
      }
      await completeUpload(uploadId)
      setMessage({ text: `${files.length} files uploaded and queued as one combined game.`, timeout: -1 })
    } catch (err) {
      setMessage({ text: `${errorText(err)} Retry to continue the same combined game.`, severity: 'error', timeout: -1 })
    } finally {
      setLoading(false)
    }
  }

  const handleUpload = async () => {
    // The API requires and records the rights confirmation that initiateUpload sends.
    if (!name.trim() || !files.length || !rightsConfirmed || loading || (combineFiles && files.length > 20)) return
    if (targets.some(target => !/^\d{0,3}$/.test(target.jersey || ''))) {
      setMessage({ text: 'Jersey numbers can have up to three digits.', severity: 'warning', timeout: -1 })
      return
    }
    // Automatic analysis needs this game's appearance; storage and later analysis do not.
    if (analyzeAfterProcessing && targets.some(target => !target.jersey || !target.uniform?.trim())) {
      setMessage({ text: 'Confirm the jersey number and uniform color for each selected player in this game.', severity: 'warning', timeout: -1 })
      return
    }

    setLoading(true)
    let currentGameId = gameId
    try {
      if (!currentGameId) {
        const game = await playerRequest('/games', 'POST', {
          name: name.trim(),
          recorded_at: dateTime.toJSDate(),
          auto_analyze: analyzeAfterProcessing,
          players: targets.map(({ id, team, jersey, uniform, position, starting_side: startingSide }) => ({ player_id: id, team, jersey, uniform: uniform || '', position: normalizePlayerPosition(position) || 'unknown', starting_side: startingSide || 'unknown' }))
        })
        currentGameId = game.id
        setGameId(game.id)
      }
    } catch (err) {
      setMessage({ text: err.message, severity: 'error', timeout: -1 }); setLoading(false); return
    }
    if (combineFiles) {
      await uploadCombined(currentGameId)
      return
    }
    let succeeded = 0
    let failed = 0
    let confirmationWarnings = 0

    for (let index = 0; index < files.length; index++) {
      const item = files[index]
      if (item.status === 'complete') { succeeded += 1; continue }
      const itemName = uploadName(item, index)

      try {
        updateFileState(item.id, { status: 'preparing', progress: 0, error: null })

        const initiated = await initiateUpload({
          name: itemName,
          gameId: currentGameId,
          date: dateTime && dateTime.toJSDate ? dateTime.toJSDate() : new Date(dateTime),
          description: description || undefined,
          file: item.file,
          groups: selectedGroups,
          sourceType,
          sequenceIndex: sourceType === 'whole_game' ? null : index + 1
        })

        updateFileState(item.id, {
          status: 'uploading',
          uploadId: initiated.upload.id
        })

        await axios.put(initiated.uploadUrl, item.file, {
          headers: {
            'Content-Type': item.file.type || 'video/quicktime'
          },
          onUploadProgress: progressEvent => {
            if (!progressEvent.total) return
            updateFileState(item.id, {
              progress: Math.min(100, Math.round((progressEvent.loaded * 100) / progressEvent.total))
            })
          }
        })

        try {
          await completeUpload(initiated.upload.id)
        } catch (confirmationError) {
          console.warn('Upload reached S3 but server confirmation was delayed.', confirmationError)
          confirmationWarnings += 1
        }

        updateFileState(item.id, { status: 'complete', progress: 100 })
        succeeded += 1
      } catch (err) {
        console.error(err)
        updateFileState(item.id, {
          status: 'error',
          error: errorText(err)
        })
        failed += 1
      }
    }

    setLoading(false)

    if (failed) {
      setMessage({
        text: `${succeeded} video${succeeded === 1 ? '' : 's'} uploaded. ${failed} failed. The successful videos are now processing.`,
        severity: 'warning',
        timeout: -1
      })
    } else if (confirmationWarnings) {
      setMessage({
        text: `${succeeded} video${succeeded === 1 ? '' : 's'} reached storage. Server confirmation is delayed for ${confirmationWarnings}; the watchdog will reconcile ${confirmationWarnings === 1 ? 'it' : 'them'} automatically.`,
        severity: 'warning',
        timeout: -1
      })
    } else {
      setMessage({
        text: `${succeeded} video${succeeded === 1 ? '' : 's'} uploaded successfully and queued for processing.`,
        timeout: -1
      })
    }
  }

  const resetForm = () => {
    setGameId(null)
    setCombinedUploadId(null)
    setTargets([])
    setAutoAnalyze(false)
    setName('')
    setDateTime(DateTime.now())
    setDescription('')
    setSourceType('whole_game')
    setFiles([])
    setSelectedGroups([])
    setRightsConfirmed(false)
    setMessage(null)
  }

  const completed = files.length > 0 && files.every(item => item.status === 'complete')
  const readyToUpload = Boolean(name.trim() && files.length && rightsConfirmed && !loading && !(combineFiles && files.length > 20))

  return (
    <>
      <Box data-testid="upload-workspace" sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <VideoLibraryOutlinedIcon color="primary" sx={{ fontSize: 34 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  Upload Game Video
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Add one video or every file of a game in a single upload session.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="primary.light" sx={{ letterSpacing: '.14em' }}>
                  VIDEO TYPE
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tell Highlights what kind of footage you are uploading.
                </Typography>
              </Box>
              <ToggleButtonGroup
                value={sourceType}
                exclusive
                onChange={selectSourceType}
                disabled={loading || Boolean(gameId)}
                fullWidth
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1,
                  '& .MuiToggleButtonGroup-grouped': {
                    border: '1px solid !important',
                    borderColor: 'divider !important',
                    borderRadius: '10px !important',
                    m: '0 !important'
                  }
                }}
              >
                {SOURCE_TYPES.map(type => (
                  <ToggleButton
                    key={type.value}
                    value={type.value}
                    sx={{
                      py: 1.5,
                      px: 1,
                      textTransform: 'none',
                      fontWeight: 700
                    }}
                  >
                    {type.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Alert severity="info" variant="outlined">
                {sourceTypeConfig.description}
              </Alert>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 3 },
              background: 'linear-gradient(145deg, rgba(20,34,45,.96), rgba(12,20,28,.98))'
            }}
          >
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="overline" color="primary.light" sx={{ letterSpacing: '.14em' }}>
                  GAME DETAILS
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enter this once. It will be reused for every selected video.
                </Typography>
              </Box>

              {playerError && <Alert severity="warning">Players could not be loaded: {playerError}</Alert>}
              <Autocomplete multiple options={players} value={targets} disabled={loading || Boolean(gameId)}
                getOptionLabel={player => player.name} isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_e, values) => setTargets(values)}
                renderInput={params => <TextField {...params} label="Players to track and analyze" helperText="Selecting players does not start analysis. Choose below whether to analyze after processing, or start it later from the game page. Leave empty for video storage only." />} />
              <Box>
                <FormControlLabel label="Analyze automatically after processing (beta)" control={<Checkbox checked={autoAnalyze}
                  disabled={loading || Boolean(gameId) || !targets.length} onChange={event => setAutoAnalyze(event.target.checked)} />} />
                <FormHelperText sx={{ mt: 0 }}>Off by default. When on, AI player tracking and analysis starts for each selected player once the video is processed. Results are a beta and may be wrong. You can start or re-run analysis later without uploading again.</FormHelperText>
              </Box>
              {!gameId && <Button component={RouterLink} to="/players" target="_blank" rel="noopener">Manage players in a new tab</Button>}
              {!gameId && <Button onClick={() => playerRequest('/players').then(rows => { setPlayers(rows); setPlayerError('') }).catch(e => setPlayerError(e.message))}>Refresh players</Button>}
              {targets.map(target => <Stack key={target.id} spacing={1}>
                  <Typography variant="subtitle2">{target.name}: confirm this game’s appearance</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  {['team', 'jersey', 'uniform'].map(field => <TextField key={field} required={analyzeAfterProcessing && field !== 'team'} label={field === 'uniform' ? 'Uniform color and trim this game' : `${target.name} ${field}`} value={target[field] || ''} helperText={field === 'uniform' ? 'Home, away, third or showcase uniform' : undefined} inputProps={{ maxLength: field === 'jersey' ? 3 : 200 }} disabled={loading || Boolean(gameId)} onChange={e => setTargets(rows => rows.map(row => row.id === target.id ? { ...row, [field]: e.target.value } : row))} />)}
                  <TextField select label="Position this game" value={normalizePlayerPosition(target.position) || 'unknown'} disabled={loading || Boolean(gameId)} onChange={e => setTargets(rows => rows.map(row => row.id === target.id ? { ...row, position: e.target.value } : row))} sx={{ minWidth: 150 }}>
                    {PLAYER_POSITION_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                  </TextField>
                </Stack>
                <TextField select label="Starting goal side" value={target.starting_side || 'unknown'} disabled={loading || Boolean(gameId)} helperText="The goal this team defends at the start of play, as seen in the video. Teams can change ends later."
                  onChange={e => setTargets(rows => rows.map(row => row.id === target.id ? { ...row, starting_side: e.target.value } : row))}>
                  {STARTING_SIDE_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </TextField>
              </Stack>)}
              {analyzeAfterProcessing && <Alert severity="info">Confirm these details once for this game. Matching and analysis (beta) start automatically after processing. Results show matched player clips or guide you through correcting the game details here in Highlights. An optional reference can help when a jersey is hard to read; identifying every clip is not required.</Alert>}
              {!!targets.length && !analyzeAfterProcessing && <Alert severity="info">Analysis will not start automatically. Details entered here are saved with the game. To start analysis later from the game page, each player needs this game’s jersey number and uniform; otherwise enter them when you start analysis from the video.</Alert>}
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    required
                    fullWidth
                    id="name"
                    label="Game / video name"
                    placeholder="Jr Flyers vs Washington Pride"
                    value={name}
                    disabled={loading || Boolean(gameId)}
                    onChange={ev => setName(ev.target.value)}
                    helperText={combineFiles
                      ? 'One title for the entire combined game.'
                      : sourceType === 'whole_game'
                        ? 'Use a name that will be easy to find later.'
                        : 'Each clip gets an automatic title that you can customize below.'}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DateTimePicker
                    sx={{ width: '100%' }}
                    slotProps={{ textField: { variant: 'outlined', fullWidth: true } }}
                    label="Game date / time"
                    value={dateTime}
                    disabled={loading || Boolean(gameId)}
                    onChange={setDateTime}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    id="description"
                    label="Description"
                    placeholder="Tournament, rink, opponent, notes..."
                    multiline
                    rows={3}
                    value={description}
                    disabled={loading || Boolean(gameId)}
                    onChange={ev => setDescription(ev.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: 'rgba(255,255,255,.02)' }}>
                    {groups.length
                      ? <GroupOptions
                          groups={groups}
                          selectedGroups={selectedGroups}
                          onGroupChange={onGroupChange}
                          disabled={loading || Boolean(gameId)}
                        />
                      : <Stack spacing={0.5}>
                          <Typography variant="subtitle2">Sharing</Typography>
                          <Typography variant="body2" color="text.secondary">
                            This upload will be private to your account.
                          </Typography>
                        </Stack>}
                  </Paper>
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="primary.light" sx={{ letterSpacing: '.14em' }}>
                  VIDEO FILES
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sourceType === 'whole_game'
                    ? 'Select a complete game or all consecutive files from the same game. Several files become one game video.'
                    : 'Select one or more clips. Each clip is saved as its own video, in the order shown.'}
                </Typography>
              </Box>

              <Box
                onDragEnter={ev => {
                  ev.preventDefault()
                  if (!loading) setDragActive(true)
                }}
                onDragOver={ev => ev.preventDefault()}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
                sx={{
                  border: '2px dashed',
                  borderColor: dragActive ? 'primary.main' : 'divider',
                  borderRadius: 3,
                  px: 3,
                  py: 4,
                  textAlign: 'center',
                  bgcolor: dragActive ? 'rgba(56,165,198,.08)' : 'rgba(255,255,255,.015)',
                  transition: '150ms ease'
                }}
              >
                <Stack spacing={1.25} alignItems="center">
                  <CloudUploadRoundedIcon color="primary" sx={{ fontSize: 46 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Drop game videos here
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sourceType === 'whole_game'
                      ? 'Choose the complete game or its consecutive recordings from your device.'
                      : 'Choose one or several video files from your device.'}
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={loading || Boolean(gameId)}
                    startIcon={<MovieCreationOutlinedIcon />}
                  >
                    Choose videos
                    <input
                      id="file"
                      ref={fileComponent}
                      hidden
                      multiple
                      accept="video/*"
                      type="file"
                      onChange={fileChanged}
                    />
                  </Button>
                </Stack>
              </Box>

              {files.length > 1 && <Alert severity={combineFiles && files.length > 20 ? 'error' : 'info'}>
                {combineFiles
                  ? files.length > 20
                    ? 'Combine up to 20 files per game. Remove some files.'
                    : 'One game video with a continuous timeline. Check the file order below. All files must finish uploading before processing starts.'
                  : 'Each clip gets its own title and playable video.'}
              </Alert>}

              {files.length
                ? <Stack spacing={1.25}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Upload queue
                      </Typography>
                      <Chip
                        size="small"
                        label={`${files.length} video${files.length === 1 ? '' : 's'}`}
                        color="primary"
                        variant="outlined"
                      />
                    </Stack>

                    {files.map((item, index) => {
                      const statusIcon = item.status === 'complete'
                        ? <CheckCircleRoundedIcon color="success" />
                        : item.status === 'error'
                          ? <ErrorOutlineRoundedIcon color="error" />
                          : <MovieCreationOutlinedIcon color="action" />

                      return (
                        <Paper key={item.id} variant="outlined" sx={{ p: 1.75, bgcolor: 'rgba(255,255,255,.015)' }}>
                          <Grid container spacing={1.5} alignItems="center">
                            <Grid item xs="auto">
                              {statusIcon}
                            </Grid>
                            <Grid item xs={12} sm={5} md={4}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                {item.file.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatBytes(item.file.size)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm>
                              {combineFiles
                                ? <Typography variant="body2">File {index + 1} of {files.length}</Typography>
                                : <TextField
                                size="small"
                                fullWidth
                                label={sourceType === 'shift_clip'
                                  ? `Shift ${index + 1} title`
                                  : sourceType === 'highlight_clip'
                                    ? `Highlight ${index + 1} title`
                                    : 'Video title'}
                                value={item.customName || uploadName(item, index)}
                                disabled={loading || Boolean(gameId)}
                                onChange={ev => updateFileName(item.id, ev.target.value)}
                              />}
                            </Grid>
                            <Grid item xs="auto">
                              {files.length > 1 && <>
                                <IconButton aria-label={`Move ${item.file.name} earlier`} disabled={loading || Boolean(gameId) || index === 0} onClick={() => moveFile(index, -1)}><ArrowUpwardRoundedIcon /></IconButton>
                                <IconButton aria-label={`Move ${item.file.name} later`} disabled={loading || Boolean(gameId) || index === files.length - 1} onClick={() => moveFile(index, 1)}><ArrowDownwardRoundedIcon /></IconButton>
                              </>}
                              <IconButton
                                aria-label={`Remove ${item.file.name}`}
                                disabled={loading || Boolean(gameId)}
                                onClick={() => removeFile(item.id)}
                              >
                                <DeleteOutlineRoundedIcon />
                              </IconButton>
                            </Grid>
                            {(item.status === 'preparing' || item.status === 'uploading' || item.status === 'complete')
                              ? <Grid item xs={12}>
                                  <Stack direction="row" spacing={1.5} alignItems="center">
                                    <LinearProgress
                                      variant="determinate"
                                      value={item.status === 'preparing' ? 2 : item.progress}
                                      sx={{ flex: 1, height: 7, borderRadius: 999 }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ width: 72, textAlign: 'right' }}>
                                      {item.status === 'preparing'
                                        ? 'Preparing'
                                        : item.status === 'complete'
                                          ? 'Uploaded'
                                          : `${item.progress}%`}
                                    </Typography>
                                  </Stack>
                                </Grid>
                              : null}
                            {item.status === 'error'
                              ? <Grid item xs={12}>
                                  <Alert severity="error">
                                    {item.error || 'Upload failed.'}
                                  </Alert>
                                </Grid>
                              : null}
                          </Grid>
                        </Paper>
                      )
                    })}
                  </Stack>
                : null}
            </Stack>
          </Paper>

          <Typography variant="body2" color="text.secondary">
            {UPLOAD_DISCLAIMER}{' '}
            <Link component={RouterLink} to={TERMS_PATH} target="_blank" rel="noopener">
              Read the Terms of Use
            </Link>
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              position: { md: 'sticky' },
              bottom: { md: 12 },
              zIndex: 2,
              bgcolor: 'rgba(17,24,32,.96)',
              backdropFilter: 'blur(12px)'
            }}
          >
            <FormControlLabel
              sx={{ mb: 1 }}
              control={<Checkbox checked={rightsConfirmed} disabled={loading} onChange={event => setRightsConfirmed(event.target.checked)} />}
              label={<Typography variant="body2">{UPLOAD_ATTESTATION}</Typography>}
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {files.length
                    ? !rightsConfirmed
                        ? UPLOAD_ATTESTATION_NEEDED
                        : combineFiles ? `${files.length} files → 1 game video` : `${files.length} video${files.length === 1 ? '' : 's'} ready as ${sourceTypeConfig.label.toLowerCase()}`
                    : 'Add at least one video to continue'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Files upload one at a time so each has a clear status.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                {(completed || gameId)
                  ? <Button variant="outlined" disabled={loading} onClick={resetForm}>
                      Upload another game
                    </Button>
                  : null}
                <LoadingButton
                  variant="contained"
                  size="large"
                  onClick={handleUpload}
                  loading={loading}
                  disabled={!readyToUpload}
                  startIcon={<CloudUploadRoundedIcon />}
                >
                  {combineFiles ? 'Upload combined game' : files.length > 1 ? `Upload ${files.length} videos` : 'Upload video'}
                </LoadingButton>
              </Stack>
            </Stack>
          </Paper>

          {gameId && <Button component={RouterLink} to={`/game/${gameId}`}>View game and processing status</Button>}
          <Divider />
          <Typography variant="caption" color="text.secondary" textAlign="center">
            {sourceType === 'whole_game' ? 'Files are joined in the order shown into one game video.' : 'Each clip is processed and shown as its own video.'}
          </Typography>
        </Stack>
      </Box>

      <Messages message={message} onClose={() => setMessage(null)} />
    </>
  )
}

export default UploadFile
